import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js';
import { cn } from '@shared/lib/cn';

/**
 * Country dial-code picker for mobile-number entry — a searchable dropdown
 * of every country with its calling code, mirroring the Angular member
 * portal's country selector. Flags are emoji (Unicode regional indicators)
 * so no flag assets are bundled; they render natively on iOS/Android.
 */

export interface CountryDial {
  /** ISO 3166-1 alpha-2 code, e.g. `ZA`. */
  code: string;
  name: string;
  /** Calling code with leading `+`, e.g. `+27`. */
  dialCode: string;
  /** Emoji flag. */
  flag: string;
}

/** Convert a 2-letter ISO country code to its emoji flag. */
function isoToFlag(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));
}

/** All dial-able countries, name-sorted. Built once at module load. */
const COUNTRIES: CountryDial[] = (() => {
  const names = new Intl.DisplayNames(['en'], { type: 'region' });
  return getCountries()
    .map((code) => {
      let dialCode = '';
      try {
        dialCode = `+${getCountryCallingCode(code as CountryCode)}`;
      } catch {
        dialCode = '';
      }
      return { code, name: names.of(code) ?? code, dialCode, flag: isoToFlag(code) };
    })
    .filter((c) => !!c.dialCode)
    .sort((a, b) => a.name.localeCompare(b.name));
})();

/** All dial-able countries, name-sorted. Read-only — exposed so callers
 *  can split an E.164 number back into its country/local parts. */
export function listCountries(): readonly CountryDial[] {
  return COUNTRIES;
}

/** Resolve an ISO code to its country, defaulting to ZA. */
export function findCountry(code: string): CountryDial {
  return (
    COUNTRIES.find((c) => c.code === code) ??
    COUNTRIES.find((c) => c.code === 'ZA') ??
    COUNTRIES[0]!
  );
}

interface CountryCodePickerProps {
  /** Selected ISO country code. */
  value: string;
  onChange: (country: CountryDial) => void;
}

export function CountryCodePicker({ value, onChange }: CountryCodePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const selected = findCountry(value);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      `${c.name} ${c.code} ${c.dialCode}`.toLowerCase().includes(q),
    );
  }, [search]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Country code, ${selected.name} ${selected.dialCode}`}
        className="flex h-12 items-center gap-1.5 rounded-md border border-hairline bg-elevated px-3 text-body text-ink"
      >
        <span className="text-[1.05rem] leading-none">{selected.flag}</span>
        <span>{selected.dialCode}</span>
        <ChevronDown className="h-4 w-4 text-ink-muted" strokeWidth={2} />
      </button>

      {open ? (
        <div className="absolute left-0 top-[calc(100%+0.25rem)] z-50 w-72 max-w-[80vw] overflow-hidden rounded-lg border border-hairline bg-card shadow-elevated">
          <div className="flex items-center gap-2 border-b border-hairline px-3 py-2">
            <Search className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              placeholder="Search countries…"
              className="w-full bg-transparent text-body-sm text-ink placeholder:text-ink-muted focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setSearch('');
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-body-sm transition-colors hover:bg-sunken',
                  c.code === selected.code && 'bg-primary-50',
                )}
              >
                <span className="text-[1.05rem] leading-none">{c.flag}</span>
                <span className="min-w-0 flex-1 truncate text-ink">{c.name}</span>
                <span className="text-ink-muted">{c.dialCode}</span>
              </button>
            ))}
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-caption text-ink-muted">No matches</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
